import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { UserProfile } from '@/types';
import { useBroadcastChannel, type BroadcastMessage } from '@/hooks/useBroadcastChannel';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isHead: boolean;
  canView: boolean;
  canEdit: boolean;
  canAccessSettings: boolean;
  canViewBankBalances: boolean;
  // Restrição de loja
  hasStoreRestriction: boolean;
  restrictedStore: string | null;
}

// Usar variável global para manter contexto estável durante HMR
// Isso evita o erro "useAuth must be used within an AuthProvider" após hot reload
const AUTH_CONTEXT_KEY = '__AUTH_CONTEXT__';
const globalForAuth = globalThis as unknown as { [AUTH_CONTEXT_KEY]?: React.Context<AuthContextType | undefined> };
const AuthContext = globalForAuth[AUTH_CONTEXT_KEY] ?? createContext<AuthContextType | undefined>(undefined);
if (!globalForAuth[AUTH_CONTEXT_KEY]) {
  globalForAuth[AUTH_CONTEXT_KEY] = AuthContext;
}

// Chave do localStorage para monitorar mudanças de auth
const AUTH_STORAGE_KEY = 'sb-rmyrcvxnptrxttwfefke-auth-token';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Flag para evitar loops infinitos de sincronização
  const isHandlingCrossTabEventRef = useRef(false);

  // Handler para mensagens de outras abas
  const handleCrossTabMessage = useCallback((message: BroadcastMessage) => {
    // Evitar processar eventos enquanto já está processando um
    if (isHandlingCrossTabEventRef.current) return;

    isHandlingCrossTabEventRef.current = true;

    try {
      switch (message.type) {
        case 'AUTH_SIGNED_OUT':
        case 'AUTH_SESSION_EXPIRED':
          // Outra aba fez logout - sincronizar estado
          console.log('[AuthContext] Cross-tab logout detected');
          setUser(null);
          setProfile(null);
          setLoading(false);
          break;

        case 'AUTH_SIGNED_IN':
          // Outra aba fez login - verificar sessão
          console.log('[AuthContext] Cross-tab login detected, checking session');
          supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user) {
              setUser(session.user);
              fetchProfile(session.user.id);
            }
          }).catch(console.error);
          break;
      }
    } finally {
      // Resetar flag após um pequeno delay
      setTimeout(() => {
        isHandlingCrossTabEventRef.current = false;
      }, 100);
    }
  }, []);

  // Inicializar BroadcastChannel
  const { postMessage } = useBroadcastChannel(handleCrossTabMessage);

  // Fetch user profile
  const fetchProfile = async (userId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        // Buscar o nome completo da tabela equipe usando o email
        let fullName = 'Usuário';
        let equipeId = null;

        if (user.email) {
          const { data: equipeData } = await supabase
            .from('equipe')
            .select('id, nome, role, ativo, cargo, loja_restrita')
            .eq('email', user.email)
            .single();

          if (equipeData) {
            // Verificar se o usuário está ativo
            if (!equipeData.ativo) {
              // Usuário inativo, fazer logout
              await supabase.auth.signOut();
              throw new Error('Sua conta foi desativada. Entre em contato com o administrador.');
            }

            fullName = equipeData.nome || user.email.split('@')[0] || 'Usuário';
            equipeId = equipeData.id;

            // Usar o role real do banco de dados
            const userRole = equipeData.role || 'colaborador';

            const defaultProfile: UserProfile = {
              id: userId,
              equipe_id: equipeId,
              full_name: fullName,
              nome: fullName, // Campo nome para compatibilidade
              role: userRole, // Role real do usuário
              cargo: equipeData.cargo || null,
              is_active: true,
              created_at: user.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
              loja_restrita: equipeData.loja_restrita || null,
            };
            setProfile(defaultProfile);
          } else {
            // Se não encontrar na equipe, usa valores padrão
            fullName = user.email.split('@')[0] || 'Usuário';

            const defaultProfile: UserProfile = {
              id: userId,
              equipe_id: equipeId,
              full_name: fullName,
              nome: fullName,
              role: 'colaborador', // Role padrão para usuários não cadastrados
              cargo: null,
              is_active: true,
              created_at: user.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
              loja_restrita: null,
            };
            setProfile(defaultProfile);
          }
        } else {
          // Sem email, criar perfil básico
          const defaultProfile: UserProfile = {
            id: userId,
            equipe_id: null,
            full_name: 'Usuário',
            nome: 'Usuário',
            role: 'colaborador',
            cargo: null,
            is_active: true,
            created_at: user.created_at || new Date().toISOString(),
            updated_at: new Date().toISOString(),
            loja_restrita: null,
          };
          setProfile(defaultProfile);
        }
      }
    } catch (err) {
      console.error('Error creating profile:', err);
    }
  };

  // Check initial session
  useEffect(() => {
    if (isInitialized) return;

    const initializeAuth = async () => {
      try {
        // Clear any existing invalid tokens first
        const storedToken = localStorage.getItem('sb-rmyrcvxnptrxttwfefke-auth-token');
        if (storedToken) {
          try {
            JSON.parse(storedToken);
          } catch {
            // Invalid token format, clear it
            localStorage.removeItem('sb-rmyrcvxnptrxttwfefke-auth-token');
          }
        }

        // Try to get the current session with timeout (increased to 15s for slower connections)
        const getSessionWithTimeout = async () => {
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('Session timeout')), 15000);
          });

          const sessionPromise = supabase.auth.getSession();

          return Promise.race([sessionPromise, timeoutPromise]);
        };

        let session = null;
        let sessionError = null;

        // Retry logic for session fetch (up to 2 attempts)
        const maxRetries = 2;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const result = await getSessionWithTimeout();
            session = result.data.session;
            sessionError = result.error;
            break; // Success, exit retry loop
          } catch (err) {
            console.log(`[AuthContext] Session attempt ${attempt}/${maxRetries} failed:`, err);
            if (attempt === maxRetries) {
              // All retries failed - clear auth token and proceed to login
              console.log('[AuthContext] All session attempts failed, redirecting to login');
              localStorage.removeItem('sb-rmyrcvxnptrxttwfefke-auth-token');
              setUser(null);
              setProfile(null);
              setLoading(false);
              setIsInitialized(true);
              return;
            }
            // Wait 1 second before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        if (sessionError) {
          console.log('Session error, clearing auth state:', sessionError.message);
          // Clear all auth-related storage
          localStorage.removeItem('sb-rmyrcvxnptrxttwfefke-auth-token');
          sessionStorage.clear();
          await supabase.auth.signOut();
          setUser(null);
          setProfile(null);
        } else if (session?.user) {
          // We have a session, set the user
          setUser(session.user);
          await fetchProfile(session.user.id);

          // Atualizar last_seen_at ao restaurar sessão (refresh/page load)
          try {
            await supabase
              .from('equipe')
              .update({ last_seen_at: new Date().toISOString() })
              .eq('auth_user_id', session.user.id);
          } catch (updateError) {
            console.error('[AuthContext] Error updating last_seen_at on restore:', updateError);
          }
        } else {
          // No session, ensure clean state
          setUser(null);
          setProfile(null);
        }
      } catch (err) {
        console.error('Session initialization error:', err);
        // Clear auth state on error
        localStorage.removeItem('sb-rmyrcvxnptrxttwfefke-auth-token');
        sessionStorage.clear();
        setUser(null);
        setProfile(null);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, [isInitialized]);

  // Listen for auth changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip if not initialized to prevent race conditions
      if (!isInitialized) return;

      // Skip if handling cross-tab event to avoid loops
      if (isHandlingCrossTabEventRef.current) return;

      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user);
        await fetchProfile(session.user.id);
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setProfile(null);
        setLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        setUser(session.user);
        await fetchProfile(session.user.id);
        setLoading(false);
      } else if (event === 'USER_UPDATED' && session?.user) {
        setUser(session.user);
        await fetchProfile(session.user.id);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [isInitialized]);

  // Listen for localStorage changes (cross-tab auth sync fallback)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Verificar se é a chave de auth do Supabase
      if (event.key !== AUTH_STORAGE_KEY) return;

      // Evitar processar enquanto já está processando
      if (isHandlingCrossTabEventRef.current) return;

      isHandlingCrossTabEventRef.current = true;

      try {
        if (!event.newValue) {
          // Token foi removido - outra aba fez logout
          console.log('[AuthContext] Auth token removed in another tab');
          setUser(null);
          setProfile(null);
          setLoading(false);
        } else if (event.newValue && !event.oldValue) {
          // Token foi adicionado - outra aba fez login
          console.log('[AuthContext] Auth token added in another tab');
          supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user) {
              setUser(session.user);
              fetchProfile(session.user.id);
            }
          }).catch(console.error);
        }
      } finally {
        setTimeout(() => {
          isHandlingCrossTabEventRef.current = false;
        }, 100);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Sign in function
  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);

      // Primeiro autenticar o usuário com timeout (increased to 20s for slower connections)
      const signInWithTimeout = async () => {
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Tempo limite excedido. Verifique sua conexão.')), 20000);
        });

        const signInPromise = supabase.auth.signInWithPassword({
          email,
          password,
        });

        return Promise.race([signInPromise, timeoutPromise]);
      };

      const { data, error } = await signInWithTimeout();

      if (error) throw error;

      // Depois verificar se o usuário está ativo (agora autenticado, pode acessar equipe)
      const { data: memberData } = await supabase
        .from('equipe')
        .select('ativo')
        .eq('email', email)
        .single();

      if (memberData && !memberData.ativo) {
        // Se inativo, fazer logout imediatamente
        await supabase.auth.signOut();
        throw new Error('Sua conta foi desativada. Entre em contato com o administrador.');
      }

      if (data.user) {
        setUser(data.user);
        await fetchProfile(data.user.id);
        setIsInitialized(true);

        // Notificar outras abas sobre o login
        postMessage('AUTH_SIGNED_IN', { userId: data.user.id });

        // Atualizar last_seen_at na tabela equipe
        try {
          await supabase
            .from('equipe')
            .update({ last_seen_at: new Date().toISOString() })
            .eq('auth_user_id', data.user.id);
        } catch (updateError) {
          console.error('[AuthContext] Error updating last_seen_at:', updateError);
          // Não falhar o login se a atualização do last_seen falhar
        }
      }
    } catch (err) {
      console.error('Sign in error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      setLoading(true);

      // Notificar outras abas ANTES do logout
      postMessage('AUTH_SIGNED_OUT');

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      setProfile(null);
    } catch (err) {
      console.error('Sign out error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign out';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Permission helpers
  const isAdmin = profile?.role === 'admin';
  const isHead = profile?.role === 'head';
  const canView = !!profile && profile.is_active;
  const canEdit = isAdmin && profile?.is_active === true; // Apenas admin pode editar
  const canAccessSettings = (isAdmin || isHead) && profile?.is_active === true; // Admin e Head
  const canViewBankBalances = isAdmin && profile?.is_active === true; // Apenas admin pode ver saldos por banco

  // Store restriction helpers
  const hasStoreRestriction = !!profile?.loja_restrita;
  const restrictedStore = profile?.loja_restrita || null;

  const value: AuthContextType = {
    user,
    profile,
    loading,
    error,
    signIn,
    signOut,
    isAdmin,
    isHead,
    canView,
    canEdit,
    canAccessSettings,
    canViewBankBalances,
    hasStoreRestriction,
    restrictedStore,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}